import { OrgToolConfig } from "@/services/v4.service";
import { DraftComponent, Editor, EditorState, getDefaultKeyBinding } from "draft-js";
import "draft-js/dist/Draft.css";
import { classNames } from "lib/utils";
import dynamic from "next/dynamic";
import { ForwardedRef, forwardRef, memo, useState } from "react";
import AutocompletePanel, { InputSuggestion } from "./AutocompletePanel";
import styles from "./PromptEditorEditor.module.css";

// Needed as DraftJS seems to have issues with NextJS
// (when using the normal EditorState.createEmpty() initialization)
// See: https://github.com/facebook/draft-js/issues/2427#issue-617805067
const EditorComponent = dynamic(() => import("./DynamicEditor"), {
  ssr: false,
});

const EditorComponentWithForwardedRef = forwardRef<Editor, DraftComponent.Base.DraftEditorProps>(
  function EditorComponentRefForwarder(props, ref) {
    return <EditorComponent {...props} forwardedRef={ref} />;
  },
);

interface OwnProps {
  handleSubmit: () => void;
  handleSave: () => void;
  editorState: EditorState;
  /** Keybinding handler. Follow draft js spec and return "handled" if stop propagation. */
  additionalKeyBindingFn?: (event: any) => "handled" | undefined;
  onChange: (editorState: EditorState) => void;
  preview: boolean;
  autocomplete?: boolean;
  availableVariables?: InputSuggestion[];
  availableTools?: OrgToolConfig[];
  className?: string;
}

type PromptEditorProps = OwnProps & DraftComponent.Base.DraftEditorProps;

const PromptEditorEditor = (
  {
    handleSubmit,
    handleSave,
    additionalKeyBindingFn,
    editorState,
    onChange,
    preview,
    className,
    autocomplete,
    availableVariables,
    availableTools,
    ...props
  }: PromptEditorProps,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const keyBindingFn = (event: any): string | null => {
    if (additionalKeyBindingFn) {
      const handled = additionalKeyBindingFn(event);
      if (handled) {
        return handled;
      }
    }
    if (event.metaKey && event.key === "Enter") {
      handleSubmit();
    } else if ((event.key === "s" && event.metaKey) || (event.key === "s" && event.ctrlKey)) {
      handleSave();
      event.preventDefault();
    } else if (event.key === "Escape") {
    } else if (panelOpen) {
      if (event.key === "ArrowUp" || event.key === "ArrowDown" || event.key === "Enter" || event.key === "Tab") {
        // Do not move cursor if pressing up or down while tool panel is open.
        return "handled"; // Returning "handled" here tells draftjs we've handled it and it does not have to also attempt to handle it.
      }
    } else {
      return getDefaultKeyBinding(event);
    }
    return null;
  };
  const hasFocus = editorState.getSelection().getHasFocus();

  // Handy debugging tools
  // const logState = () => console.log(editorState.toJS());
  // const undoStack = editorState.getUndoStack();
  // const redoStack = editorState.getRedoStack();

  return (
    <div
      className={classNames(
        "font-content relative flex h-full w-full flex-col",
        preview && "bg-oldgray-100/90",
        styles.editor,
        className,
      )}
    >
      {/* <pre className="h-200 overflow-auto whitespace-pre-wrap text-10-10">
        {JSON.stringify({ autocomplete, hasInputs, availableTools, test: 12 }, null, 2)}
      </pre> */}
      {/* <div className="flex items-center gap-20">
        <pre>
          {undoStack.length} / {redoStack.length}
        </pre>
        <Button onClick={logState}>Log state</Button>
      </div> */}
      <EditorComponentWithForwardedRef
        editorKey="playground"
        editorState={editorState}
        keyBindingFn={keyBindingFn}
        stripPastedStyles
        onChange={onChange}
        {...props}
        // @ts-ignore
        ref={ref}
      />
      {autocomplete && (
        <AutocompletePanel
          open={panelOpen}
          setOpen={setPanelOpen}
          editorState={editorState}
          setEditorState={onChange}
          availableTools={availableTools}
          availableVariables={availableVariables}
          enabled={hasFocus}
        />
      )}

      {preview && <PreviewBar />}
    </div>
  );
};
const PreviewBar = ({}) => {
  return <div className="w-full bg-oldgray-300 px-12 py-8 text-16-24 text-oldgray-700">Previewing</div>;
};

export default memo(forwardRef(PromptEditorEditor));
